// Step 5d Phase 1 — IndexedDB offline read cache (schema + open).
//
// Single versioned DB, explicit upgrade path. Phase 1 stores exactly what the board
// loads (write-through), so the board stays READABLE offline. Op queue / change-feed
// cursors / offline auth profiles are later phases — but `cursor` is in the scope
// schema NOW so Phase 3 needs no DB migration.
//
// Values are stored VERBATIM as the API returned them (no reshaping) — the board
// already consumes these shapes.

import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { Envelope } from './crypto';

export const OFFLINE_DB_NAME = 'avsuite-offline-v1';
// v3 (Phase 4): + device/profiles stores for offline auth; the re-fetchable data caches
// (scopes/bookings/reference/session) are WIPED on upgrade so no plaintext survives the
// introduction of encryption-at-rest; op_queue is PRESERVED (non-re-fetchable un-synced
// work — ruling #4) and carried forward, then re-encrypted once the DEK exists post-enrol.
export const OFFLINE_DB_VERSION = 3;

export interface ScopeRecord {
  /** Canonical scope string, e.g. `bookings:all:{fromISO}:{toISO}` or `ref:aircraft`. */
  key: string;
  params: Record<string, string>;
  refreshed_at: string; // ISO — drives the offline data-age banner
  item_count: number;
  /**
   * Bookings scopes only: exact id membership of the last successful fetch.
   * Replace-by-scope deletes rely on this (a scope refresh removes rows that
   * vanished from the response — cancelled-elsewhere bookings must not linger).
   */
  ids?: string[];
  /**
   * Bookings scopes only: the opaque change-feed cursor (5d Phase 3b). The server-encoded
   * keyset position this scope has caught up to; stored + echoed verbatim, never parsed.
   * null until the first feed check establishes a baseline (or after a Phase-4 wipe).
   * No OFFLINE_DB_VERSION bump — the field has lived in the encrypted scope payload since
   * Phase 1; this only widens its TS type from `null` to `string | null`.
   */
  cursor: string | null;
}

interface SessionRecord {
  user: unknown; // AuthUser shape — profile only, NEVER tokens
  saved_at: string;
}

// One queued offline op (Phase 2, v2 §3.1). key = `${user_id}:${seq6}` — zero-padded
// seq so IDB key order IS per-user FIFO order. payload is the exact JSON the online
// endpoint takes (+ task_id for edit/cancel).
export interface QueuedOp {
  key: string;
  user_id: string;
  seq: number;
  client_op_id: string;   // UUID — server idempotency key
  device_id: string;
  op_type: 'booking_create' | 'booking_edit' | 'booking_cancel'
    | 'maintenance_return' | 'relocation' | 'waitlist_join' | 'waitlist_leave'; // 5d-p2b
  payload: Record<string, unknown>;
  read_stamp: string | null;  // bookings-scope refreshed_at when the user acted
  client_created_at: string;
  /**
   * 5d Phase 5 — staleness lens. Set when a per-action staleness advisory fired at queue
   * time (the action was taken on data older than its class threshold). Client-only — NEVER
   * sent to the server (the drain forwards only the op envelope fields); read by Phase-6 beta
   * instrumentation to measure how often offline actions ran on stale data.
   */
  staleness_advised?: { class: 'A' | 'B'; age_ms: number };
}

// Phase 4: the encryption layer stores an { iv, ct } envelope as the value (with the
// keyPath field kept plaintext where the store has an in-line key). The TS value types
// below describe the DECRYPTED shape callers see; at rest the value is an Envelope.

/** Device enrolment record (single row, key 'this_device'). */
export interface DeviceRecord {
  device_id: string;
  enrolled_at: string;
  persistent_storage: boolean | null;
  /** ISO of last successful ONLINE auth — drives the uniform 7-day offline window. */
  last_online_auth_at: string;
}

/** Per-user PIN profile (key = user_id). Salt/params/wrapped-DEK + lockout state. */
export interface ProfileRecord {
  user_id: string;
  display_name: string;
  salt: Uint8Array;
  kdf: { algorithm: string; iterations: number; memorySizeKiB: number; parallelism: number };
  wrapped_dek: { iv: Uint8Array; ct: ArrayBuffer };
  failed_attempts: number;
  locked_until: number | null;   // epoch ms of an escalating-delay lockout (transient)
  terminal_locked: boolean;      // true after 10 failures → online re-auth required
  created_at: string;
}

interface OfflineDB extends DBSchema {
  // At rest: { key (plaintext), env } — the ScopeRecord metadata is encrypted (Phase 4).
  scopes: { key: string; value: { key: string; env: Envelope } };
  // value is an Envelope at rest (Phase 4); keyPath `id` stays plaintext for lookup. The
  // by_* indexes were declared in Phase 1 but never queried (reads are by scope.ids), and
  // are DROPPED in v3 since the value is now ciphertext (can't index encrypted fields).
  bookings: { key: string; value: unknown };
  /** Out-of-line key `${collection}:${id}` — encrypted reference rows. */
  reference: { key: string; value: unknown };
  /** Last-known auth profile for offline session restore (key 'last_user'); encrypted. */
  session: { key: string; value: unknown };
  /** Per-user FIFO offline op queue (Phase 2) — key `${user_id}:${seq6}`; payload encrypted. */
  op_queue: { key: string; value: unknown };
  /** Single device-enrolment row (key 'this_device'). */
  device: { key: string; value: DeviceRecord };
  /** Per-user PIN profiles (key = user_id). */
  profiles: { key: string; value: ProfileRecord };
}

export type OfflineDb = IDBPDatabase<OfflineDB>;

let dbPromise: Promise<OfflineDb> | null = null;

export function getOfflineDb(): Promise<OfflineDb> {
  if (!dbPromise) {
    dbPromise = openDB<OfflineDB>(OFFLINE_DB_NAME, OFFLINE_DB_VERSION, {
      upgrade(db, oldVersion, _newVersion, tx) {
        if (oldVersion < 1) {
          db.createObjectStore('scopes', { keyPath: 'key' });
          db.createObjectStore('reference'); // out-of-line keys: `${collection}:${id}`
          db.createObjectStore('session');
        }
        if (oldVersion < 2) {
          db.createObjectStore('op_queue', { keyPath: 'key' }); // Phase 2 write-offline
        }
        if (oldVersion < 3) {
          // Phase 4 — encryption-at-rest. The `bookings` store becomes OUT-OF-LINE (value is
          // a ciphertext envelope with no in-line `id`), so it is (re)created here for BOTH
          // fresh and upgrading installs; the unused Phase-1 by_* indexes are dropped with it.
          if (db.objectStoreNames.contains('bookings')) db.deleteObjectStore('bookings');
          db.createObjectStore('bookings'); // out-of-line (id passed explicitly on put)
          // WIPE the re-fetchable data caches on UPGRADE so no plaintext survives the
          // introduction of encryption (they refetch online post-enrolment). op_queue is
          // PRESERVED (ruling #4 — non-re-fetchable un-synced work); its legacy plaintext
          // rows are tolerated by the reader and re-encrypted once the DEK exists (opQueue).
          if (oldVersion >= 1) {
            tx.objectStore('scopes').clear();
            tx.objectStore('reference').clear();
            tx.objectStore('session').clear();
          }
          db.createObjectStore('device');
          db.createObjectStore('profiles', { keyPath: 'user_id' });
        }
        // Future versions: add `if (oldVersion < N)` blocks — never recreate stores.
      },
      blocked() {
        console.warn('[offline] avsuite-offline-v1 upgrade blocked by another open tab');
      },
    });
    // A failed open (e.g. storage denied) must not wedge future attempts.
    dbPromise.catch(() => { dbPromise = null; });
  }
  return dbPromise;
}

// Close the cached connection and drop the memo so a subsequent getOfflineDb() reopens
// (and re-runs the upgrade). Required before deleteDB() during a revocation purge —
// IndexedDB cannot delete a database while a connection is open.
export async function closeOfflineDb(): Promise<void> {
  if (!dbPromise) return;
  try { (await dbPromise).close(); } catch { /* already closed */ }
  dbPromise = null;
}
